"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../../contexts/AuthContext"

const isUnknownErrorValue = (value) => {
  const normalized = String(value || "").trim().toLowerCase()
  return !normalized || normalized === "unknown" || normalized === "unknown_error" || normalized === "unknown error"
}

const compactUnique = (values) => {
  const result = []

  values
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .forEach((value) => {
      const key = value.toLowerCase()
      if (result.some((existing) => existing.toLowerCase().includes(key))) return

      for (let index = result.length - 1; index >= 0; index -= 1) {
        if (key.includes(result[index].toLowerCase())) result.splice(index, 1)
      }

      result.push(value)
    })

  return result
}

const getFailedMessageError = (message = {}) => {
  const code = isUnknownErrorValue(message.errorCode) ? "Provider" : String(message.errorCode)
  const textParts = compactUnique([message.errorTitle, message.errorMessage, message.errorDetailsText])
  const text = textParts.length ? textParts.join(" - ") : "Provider did not provide a failure reason yet"
  const extraParts = compactUnique([message.errorType, message.errorSubcode ? `Subcode ${message.errorSubcode}` : "", message.httpStatus ? `HTTP ${message.httpStatus}` : ""])

  return {
    code,
    text,
    extra: extraParts.join(" · "),
    title: compactUnique([code, text, ...extraParts]).join(" | "),
  }
}

const hasMessageStatusDetails = (message = {}) =>
  [
    message.errorCode,
    message.errorTitle,
    message.errorMessage,
    message.errorDetailsText,
    message.errorType,
    message.errorSubcode,
    message.httpStatus,
  ].some((value) => !isUnknownErrorValue(value))

const useDebouncedValue = (value, delay = 450) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])

  return debouncedValue
}

const APIreport = () => {
  const navigate = useNavigate()
  const { fetchReportStats, fetchCampaigns, fetchCampaignMessages, exportCampaignReport, currentUser, getAllUsers } = useAuth()

  // State management
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    delivered: 0,
    read: 0,
    failed: 0,
    pending: 0,
    creditsUsed: 0,
    totalCost: 0,
    campaigns: 0,
    totalSent: 0,
    last24: {
      totalSent: 0,
      failed: 0,
    },
  })
  const [campaigns, setCampaigns] = useState([])
  const [loading, setLoading] = useState(false)
  const [campaignsLoading, setCampaignsLoading] = useState(false)

  // Filter states - removed sendVia filter since this is API-only
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    status: "all",
    search: "",
    numberSearch: "",
    operator: "all",
    sendVia: "api", // hardcoded to API only
  })

  const [users, setUsers] = useState([])

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  // Modal states
  const [showMessagesModal, setShowMessagesModal] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [campaignMessages, setCampaignMessages] = useState([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messagesPage, setMessagesPage] = useState(1)
  const [messagesTotalPages, setMessagesTotalPages] = useState(1)
  const [messagesTotalCount, setMessagesTotalCount] = useState(0)
  const [messagesPerPage, setMessagesPerPage] = useState(20)
  const [messagesNumberSearch, setMessagesNumberSearch] = useState("")
  const [messagesStatusFilter, setMessagesStatusFilter] = useState("all")
  const [refreshing, setRefreshing] = useState(false)
  const hasCampaignDataRef = useRef(false)
  const debouncedSearch = useDebouncedValue(filters.search)
  const debouncedNumberSearch = useDebouncedValue(filters.numberSearch)
  const activeFilters = useMemo(
    () => ({
      ...filters,
      search: debouncedSearch,
      numberSearch: debouncedNumberSearch,
    }),
    [filters, debouncedNumberSearch, debouncedSearch],
  )

  useEffect(() => {
    loadReportStats()
    loadCampaigns()
  }, [activeFilters, currentPage, itemsPerPage])

  useEffect(() => {
    const loadUsers = async () => {
      if (!currentUser) return
      if (["admin", "superadmin", "reseller", "company"].includes(currentUser.role)) {
        const res = await getAllUsers()
        if (res.success) setUsers(res.users || [])
      }
    }
    loadUsers()
  }, [currentUser])

  useEffect(() => {
    hasCampaignDataRef.current = campaigns.length > 0
  }, [campaigns.length])

  const loadReportStats = async () => {
    try {
      const apiFilters = { ...activeFilters, sendVia: "api" }
      const statsData = await fetchReportStats(apiFilters)
      setStats(statsData)
    } catch (error) {
      console.error("Error loading API stats:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadCampaigns = async ({ silent = false } = {}) => {
    const showInitialLoader = !silent || !hasCampaignDataRef.current

    if (showInitialLoader) {
      setCampaignsLoading(true)
    } else {
      setRefreshing(true)
    }

    try {
      const apiFilters = { ...activeFilters, sendVia: "api" }
      const response = await fetchCampaigns(apiFilters, currentPage, itemsPerPage)
      const campaignsData = response.data || response.campaigns || response || []
      const paginationData = response.pagination || response.meta || {}

      setCampaigns(campaignsData)
      setTotalPages(
        paginationData.pages || paginationData.totalPages || Math.ceil((paginationData.total || 0) / itemsPerPage) || 1,
      )
      setTotalItems(paginationData.total || paginationData.totalItems || campaignsData.length || 0)
    } catch (error) {
      console.error("Error loading API campaigns:", error)
      if (!hasCampaignDataRef.current) {
        setCampaigns([])
        setTotalPages(1)
        setTotalItems(0)
      }
    } finally {
      setCampaignsLoading(false)
      setRefreshing(false)
    }
  }

  const getEffectiveMessagesLimit = (perPage = messagesPerPage, campaign = selectedCampaign) => {
    if (perPage === "all") {
      return Math.max(
        messagesTotalCount ||
          campaign?.stats?.total ||
          campaign?.statusCounts?.total ||
          campaign?.totalMessages ||
          100000,
        1,
      )
    }

    return Number(perPage) || 20
  }

  const loadCampaignMessages = async (
    campaignId,
    page = 1,
    perPage = messagesPerPage,
    numberSearch = messagesNumberSearch,
    campaign = selectedCampaign,
    statusFilter = messagesStatusFilter,
  ) => {
    setMessagesLoading(true)
    const effectiveLimit = getEffectiveMessagesLimit(perPage, campaign)
    try {
      const response = await fetchCampaignMessages(campaignId, page, effectiveLimit, {
        numberSearch,
        status: statusFilter,
      })
      const messagesData = response.data || response.messages || response || []
      const paginationData = response.pagination || response.meta || {}

      setCampaignMessages(messagesData)
      setMessagesTotalPages(
        perPage === "all"
          ? 1
          : paginationData.pages ||
              paginationData.totalPages ||
              Math.ceil((paginationData.total || 0) / effectiveLimit) ||
              1,
      )
      setMessagesTotalCount(paginationData.total || paginationData.totalItems || 0)
      setMessagesPage(page)
    } catch (error) {
      console.error("Error loading campaign messages:", error)
      setCampaignMessages([])
      setMessagesTotalPages(1)
      setMessagesTotalCount(0)
    } finally {
      setMessagesLoading(false)
    }
  }

  const handleFilterChange = (key, value) => {
    if (key === "sendVia") return
    setFilters((prev) => ({ ...prev, [key]: value }))
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  const openMessagesModal = (campaign) => {
    const initialNumberSearch = filters.numberSearch || ""
    setSelectedCampaign(campaign)
    setShowMessagesModal(true)
    setCampaignMessages([])
    setMessagesPage(1)
    setMessagesTotalPages(1)
    setMessagesTotalCount(0)
    setMessagesPerPage(20)
    setMessagesNumberSearch(initialNumberSearch)
    setMessagesStatusFilter("all")
    loadCampaignMessages(campaign._id, 1, 20, initialNumberSearch, campaign, "all")
  }

  const handleMessagesPerPageChange = (value) => {
    const nextPerPage = value === "all" ? "all" : Number(value)
    setMessagesPerPage(nextPerPage)
    if (selectedCampaign) {
      loadCampaignMessages(selectedCampaign._id || selectedCampaign.id, 1, nextPerPage, messagesNumberSearch)
    }
  }

  const handleMessagesSearchSubmit = (event) => {
    event.preventDefault()
    if (selectedCampaign) {
      loadCampaignMessages(selectedCampaign._id || selectedCampaign.id, 1, messagesPerPage, messagesNumberSearch)
    }
  }

  const handleMessagesStatusChange = (status) => {
    setMessagesStatusFilter(status)
    if (selectedCampaign) {
      loadCampaignMessages(selectedCampaign._id || selectedCampaign.id, 1, messagesPerPage, messagesNumberSearch, selectedCampaign, status)
    }
  }

  const anyShowCredits = (campaigns || []).some((c) => c?.showCredits !== false)

  const closeMessagesModal = () => {
    setShowMessagesModal(false)
    setSelectedCampaign(null)
    setCampaignMessages([])
    setMessagesPage(1)
    setMessagesTotalPages(1)
    setMessagesTotalCount(0)
    setMessagesPerPage(20)
    setMessagesNumberSearch("")
    setMessagesStatusFilter("all")
    setMessagesLoading(false)
  }

  const handleExport = async (campaignId, format) => {
    try {
      if (format === "excel") {
        await exportCampaignReport(campaignId, format)
      } else {
        const data = await exportCampaignReport(campaignId, format)
        if (format === "pdf") {
          generatePDF(data, campaignId)
        } else if (format === "csv") {
          generateCSV(data, campaignId)
        }
      }
    } catch (error) {
      console.error("Export failed:", error)
    }
  }

  const generatePDF = (data, campaignId) => {
    const campaign = data.campaign || selectedCampaign || {}
    const messages = data.data || data.messages || []
    const showCredits = campaign.showCredits !== false
    const creditsStatsRow = showCredits
      ? `<tr><td>Credits Used</td><td>${data.stats?.creditsUsed || campaign.stats?.creditsUsed || 0}</td></tr>`
      : ""
    const messagesHeaderCredit = showCredits ? `<th>Credit Used</th>` : ""

    const printWindow = window.open("", "_blank")
    printWindow.document.write(`
      <html>
        <head>
          <title>Campaign Report - ${campaign.campaignName || campaign.name || campaignId}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .stats-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .stats-table th, .stats-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .stats-table th { background-color: #f2f2f2; }
            .messages-table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 12px; }
            .messages-table th, .messages-table td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            .messages-table th { background-color: #f2f2f2; }
            .status-sent { color: #28a745; font-weight: bold; }
            .status-delivered { color: #17a2b8; font-weight: bold; }
            .status-failed { color: #dc3545; font-weight: bold; }
            .footer { margin-top: 30px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Campaign Report</h1>
            <h2>${campaign.campaignName || campaign.name || "Campaign"}</h2>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Template:</strong> ${campaign.templateName || "N/A"}</p>
            <p><strong>Status:</strong> ${(campaign.status || "Unknown").toUpperCase()}</p>
            
            <p><strong>Send Via:</strong> ${(campaign.sendVia || "N/A").toUpperCase()}</p>
          </div>
          
          <h3>Campaign Statistics</h3>
          <table class="stats-table">
            <tr><th>Metric</th><th>Count</th></tr>
            <tr><td>Total Messages</td><td>${data.stats?.total || campaign.stats?.total || 0}</td></tr>
            <tr><td>Sent</td><td>${data.stats?.sent || campaign.stats?.sent || 0}</td></tr>
            <tr><td>Delivered</td><td>${data.stats?.delivered || campaign.stats?.delivered || 0}</td></tr>
            <tr><td>Read</td><td>${data.stats?.read || campaign.stats?.read || 0}</td></tr>
            <tr><td>Failed</td><td>${data.stats?.failed || campaign.stats?.failed || 0}</td></tr>
            ${creditsStatsRow}
          </table>

          ${
            messages.length > 0
              ? `
            <h3>Message Details (${messages.length} messages)</h3>
            <table class="messages-table">
              <tr>
                <th>Phone Number</th>
                <th>Status</th>
                <th>Sent Time</th>
                ${messagesHeaderCredit}
              </tr>
              ${messages
                .map(
                  (msg) => `
                <tr>
                  <td>${msg.number || msg.phoneNumber || "N/A"}</td>
                  <td class="status-${(msg.status || "pending").toLowerCase()}">${(msg.status || "pending").toUpperCase()}</td>
                  <td>${msg.sentAt ? new Date(msg.sentAt).toLocaleString() : "N/A"}</td>
                  ${showCredits ? `<td>${msg.creditUsed || msg.credits || 1}</td>` : ""}
                </tr>
              `,
                )
                .join("")}
            </table>
          `
              : "<p><em>No message details available</em></p>"
          }

          <div class="footer">
            <p>Report generated by Campaign Management System</p>
          </div>
        </body>
      </html>
    `)
    printWindow.document.close()
    printWindow.print()
  }

  const generateCSV = (data, campaignId) => {
    const campaign = data.campaign || selectedCampaign || {}
    const messages = data.data || data.messages || []
    const showCredits = campaign.showCredits !== false

    const csvData = [
      ["Campaign Report"],
      ["Campaign Name", campaign.campaignName || campaign.name || "Unknown"],
      ["Template", campaign.templateName || "N/A"],
      ["Status", (campaign.status || "Unknown").toUpperCase()],
      ["Send Via", (campaign.sendVia || "N/A").toUpperCase()],
      ["Generated", new Date().toLocaleString()],
      [""],
      ["Statistics"],
      ["Total Messages", data.stats?.total || campaign.stats?.total || 0],
      ["Sent", data.stats?.sent || campaign.stats?.sent || 0],
      ["Delivered", data.stats?.delivered || campaign.stats?.delivered || 0],
      ["Read", data.stats?.read || campaign.stats?.read || 0],
      ["Failed", data.stats?.failed || campaign.stats?.failed || 0],
    ]

    if (showCredits) {
      csvData.push(["Credits Used", data.stats?.creditsUsed || campaign.stats?.creditsUsed || 0])
    }

    csvData.push([""], ["Message Details"])

    const headerRow = ["Phone Number", "Status", "Sent Time"]
    if (showCredits) headerRow.push("Credit Used")
    csvData.push(headerRow)

    messages.forEach((msg) => {
      const row = [msg.number || msg.phoneNumber || "N/A", (msg.status || "pending").toUpperCase(), msg.sentAt ? new Date(msg.sentAt).toLocaleString() : "N/A"]
      if (showCredits) row.push(msg.creditUsed || msg.credits || 1)
      csvData.push(row)
    })

    const csvString = csvData.map((row) => row.map((field) => `"${field}"`).join(",")).join("\n")

    const blob = new Blob([csvString], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `campaign_report_${campaign.campaignName || campaignId}_${Date.now()}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const getStatusBadge = (status) => {
    const statusClasses = {
      completed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      active: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
      paused: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
      failed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
      pending: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
    }
    return statusClasses[status] || "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300"
  }

  const getOperatorBadge = (operator) => {
    const operatorClasses = {
      cloud: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
      karix: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
      pinacle: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300",
    }
    return operatorClasses[operator] || "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300"
  }

  const getSendViaBadge = (sendVia) => {
    const sendViaClasses = {
      panel: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      api: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
    }
    return sendViaClasses[sendVia] || "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300"
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading API reports...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-800">
      <div className="p-6">
        {/* Header - updated for API reports */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
            <svg className="w-8 h-8 mr-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            API Reports & Analytics
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            API campaign performance and message analytics with detailed metrics
          </p>
        </div>

        {/* Filters - removed Send Via filter since this is API-only */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {currentUser && ["admin", "superadmin", "reseller", "company"].includes(currentUser.role) && (
              <div className="sm:col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">User</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white text-sm"
                  value={filters.userId || "all"}
                  onChange={(e) => handleFilterChange("userId", e.target.value === "all" ? "" : e.target.value)}
                >
                  <option value="all">All Users</option>
                  {users.map((u) => (
                    <option key={u._id || u.id} value={u._id || u.id}>
                      {u.name || u.username || u.email || u._id}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Date</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white text-sm"
                value={filters.startDate}
                onChange={(e) => handleFilterChange("startDate", e.target.value)}
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">End Date</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white text-sm"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white text-sm"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="sending">Sending</option>
                <option value="queued">Queued</option>
                <option value="draft">Draft</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Operator</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white text-sm"
                value={filters.operator}
                onChange={(e) => handleFilterChange("operator", e.target.value)}
              disabled>
                <option value="all">All Operators</option>
                
              </select>
            </div>
            {currentUser && (currentUser.role === "admin" || currentUser.role === "superadmin") && (
              <div className="sm:col-span-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">User</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white text-sm"
                  value={filters.userId || "all"}
                  onChange={(e) => handleFilterChange("userId", e.target.value === "all" ? "" : e.target.value)}
                >
                  <option value="all">All Users</option>
                  {users.map((u) => (
                    <option key={u._id || u.id} value={u._id || u.id}>
                      {u.name || u.email || u._id}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Campaign Search</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white text-sm"
                placeholder="Search campaign/template..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Number Search</label>
              <input
                type="text"
                inputMode="tel"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white text-sm"
                placeholder="Search phone number..."
                value={filters.numberSearch}
                onChange={(e) => handleFilterChange("numberSearch", e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <span className="inline-flex px-3 py-1 text-sm font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300 rounded-full">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              API Reports Only
            </span>
          </div>
        </div>

        {/* Stats Cards - updated colors for API theme */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-full bg-orange-100 dark:bg-orange-900 mr-3 sm:mr-4">
                <svg
                  className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600 dark:text-orange-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white mb-1">Total Sent</h3>
                <p className="text-xl sm:text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {stats.totalSent ?? 0}
                </p>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  Last 24 hours total sent: {stats.last24?.totalSent ?? 0}
                </p>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  Failed total: {stats.failed ?? 0} | Last 24 hours failed: {stats.last24?.failed ?? 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-full bg-green-100 dark:bg-green-900 mr-3 sm:mr-4">
                <svg
                  className="w-4 h-4 sm:w-6 sm:h-6 text-green-600 dark:text-green-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white mb-1">Delivered</h3>
                <p className="text-xl sm:text-3xl font-bold text-green-600 dark:text-green-400">
                  {stats.delivered || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-full bg-purple-100 dark:bg-purple-900 mr-3 sm:mr-4">
                <svg
                  className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white mb-1">Read Rate</h3>
                <p className="text-xl sm:text-3xl font-bold text-purple-600 dark:text-purple-400">{stats.read || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 rounded-full bg-red-100 dark:bg-red-900 mr-3 sm:mr-4">
                <svg
                  className="w-4 h-4 sm:w-6 sm:h-6 text-red-600 dark:text-red-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white mb-1">Failed</h3>
                <p className="text-xl sm:text-3xl font-bold text-red-600 dark:text-red-400">{stats.failed || 0}</p>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  Last 24 hours: {stats.last24?.failed ?? 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* API Campaigns Table - updated title and removed Send Via column since all are API */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              API Campaign Reports
              {totalItems > 0 && (
                <span className="ml-2 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300 rounded-full">
                  {totalItems} API campaigns
                </span>
              )}
            </h2>
          </div>
          <div className="overflow-x-auto">
            {campaignsLoading && campaigns.length === 0 ? (
              <div className="text-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">Loading API campaigns...</p>
              </div>
            ) : (
              <>
                {refreshing && (
                  <div className="px-4 sm:px-6 py-2 text-xs sm:text-sm text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 border-b border-gray-200 dark:border-gray-700">
                    Refreshing API campaign data...
                  </div>
                )}
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Template
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                   
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Sent
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Delivered
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Read
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Failed
                    </th>
                    {anyShowCredits && (
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Credits
                      </th>
                    )}
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {campaigns.length === 0 ? (
                    <tr>
                      <td colSpan={anyShowCredits ? 11 : 10} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <svg
                            className="w-12 h-12 text-gray-400 mb-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                          </svg>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            No API campaigns found
                          </h3>
                          <p className="text-gray-500 dark:text-gray-400">
                            Try adjusting your filters or create a new API campaign
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    campaigns.map((campaign) => (
                      <tr key={campaign._id || campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {campaign.campaignName || campaign.name || "Unnamed Campaign"}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {campaign.createdAt ? new Date(campaign.createdAt).toLocaleDateString() : "No date"}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {campaign.templateName || campaign.template?.name || "-"}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(campaign.status || "pending")}`}
                          >
                            {(campaign.status || "pending").toUpperCase()}
                          </span>
                        </td>
                        
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {campaign.stats?.total || campaign.statusCounts?.total || 0}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {campaign.stats?.sent || campaign.statusCounts?.sent || 0}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {campaign.stats?.delivered || campaign.statusCounts?.delivered || 0}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {campaign.stats?.read || campaign.statusCounts?.read || 0}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {campaign.stats?.failed || campaign.statusCounts?.failed || 0}
                        </td>
                        {anyShowCredits && (
                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {campaign.showCredits === false ? "-" : campaign.creditsUsed || campaign.stats?.creditsUsed || 0}
                          </td>
                        )}
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openMessagesModal(campaign)}
                              className="text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300"
                              title="View Messages"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => navigate(`/campaign-conversational-report?campaignId=${campaign._id || campaign.id}`)}
                              className="text-teal-600 hover:text-teal-900 dark:text-teal-400 dark:hover:text-teal-300"
                              title="Conversational Report"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleExport(campaign._id || campaign.id, "excel")}
                              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                              title="Export"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                  </tbody>
                </table>
              </>
            )}
          </div>

          {/* Improved mobile responsive pagination */}
          {/* Pagination */}
          {totalItems > 0 && (
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} campaigns
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Previous
                </button>
                <span className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Messages Modal */}
        {showMessagesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                  <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Campaign Messages: {selectedCampaign?.campaignName || selectedCampaign?.name || "Campaign Details"}
                </h2>
                <button
                  onClick={closeMessagesModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {messagesLoading ? (
                  <div className="text-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">Loading campaign messages...</p>
                  </div>
                ) : (
                  <>
                    <form
                      onSubmit={handleMessagesSearchSubmit}
                      className="mb-4 grid grid-cols-1 md:grid-cols-[minmax(200px,1fr)_150px_150px_auto_auto] gap-3 items-end"
                    >
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          Search Number
                        </label>
                        <input
                          type="text"
                          inputMode="tel"
                          value={messagesNumberSearch}
                          onChange={(e) => setMessagesNumberSearch(e.target.value)}
                          placeholder="Find phone number..."
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          Status
                        </label>
                        <select
                          value={messagesStatusFilter}
                          onChange={(e) => handleMessagesStatusChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="all">All Status</option>
                          <option value="pending">Pending</option>
                          <option value="sent">Sent</option>
                          <option value="delivered">Delivered</option>
                          <option value="read">Read</option>
                          <option value="failed">Failed</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                          Messages Per Page
                        </label>
                        <select
                          value={messagesPerPage}
                          onChange={(e) => handleMessagesPerPageChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value={20}>20 per page</option>
                          <option value={50}>50 per page</option>
                          <option value={100}>100 per page</option>
                          <option value={500}>500 per page</option>
                          <option value={1000}>1000 per page</option>
                          <option value="all">See all</option>
                        </select>
                      </div>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium bg-orange-600 hover:bg-orange-700 text-white rounded-md"
                      >
                        Search
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMessagesNumberSearch("")
                          setMessagesStatusFilter("all")
                          if (selectedCampaign) {
                            loadCampaignMessages(selectedCampaign._id || selectedCampaign.id, 1, messagesPerPage, "", selectedCampaign, "all")
                          }
                        }}
                        className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                      >
                        Clear
                      </button>
                    </form>
                    {messagesTotalCount > 0 && (
                      <div className="mb-4 flex justify-between items-center">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Showing{" "}
                          <strong>
                            {messagesPerPage === "all" ? 1 : (messagesPage - 1) * getEffectiveMessagesLimit() + 1}
                          </strong>{" "}
                          to{" "}
                          <strong>
                            {messagesPerPage === "all"
                              ? messagesTotalCount
                              : Math.min(messagesPage * getEffectiveMessagesLimit(), messagesTotalCount)}
                          </strong>{" "}
                          of{" "}
                          <strong>{messagesTotalCount}</strong> messages
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() =>
                              loadCampaignMessages(
                                selectedCampaign._id || selectedCampaign.id,
                                messagesPage - 1,
                                messagesPerPage,
                                messagesNumberSearch,
                              )
                            }
                            disabled={messagesPage === 1 || messagesPerPage === "all"}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                          >
                            Previous
                          </button>
                          <span className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400">
                            {messagesPage}/{messagesTotalPages}
                          </span>
                          <button
                            onClick={() =>
                              loadCampaignMessages(
                                selectedCampaign._id || selectedCampaign.id,
                                messagesPage + 1,
                                messagesPerPage,
                                messagesNumberSearch,
                              )
                            }
                            disabled={messagesPage === messagesTotalPages || messagesPerPage === "all"}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Phone Number
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Sent Time
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Error Details
                            </th>
                            {selectedCampaign?.showCredits !== false && (
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Credit Used
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {campaignMessages.length === 0 ? (
                            <tr>
                              <td colSpan={selectedCampaign?.showCredits !== false ? 5 : 4} className="px-6 py-12 text-center">
                                <div className="flex flex-col items-center">
                                  <svg
                                    className="w-12 h-12 text-gray-400 mb-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                    />
                                  </svg>
                                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                    No messages found
                                  </h3>
                                  <p className="text-gray-500 dark:text-gray-400">This campaign has no messages yet</p>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            campaignMessages.map((message, index) => (
                              <tr
                                key={message._id || message.id || index}
                                className="hover:bg-gray-50 dark:hover:bg-gray-700"
                              >
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white">
                                  {message.number || message.phoneNumber || message.to || "N/A"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      message.status === "delivered"
                                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                        : message.status === "read"
                                          ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                                          : message.status === "failed" && message.refundStatus === "refunded"
                                            ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                                            : message.status === "failed"
                                              ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                                              : message.status === "sent"
                                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                                                : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300"
                                    }`}
                                  >
                                    {message.status === "failed" && message.refundStatus === "pending"
                                      ? "FAILED (refund pending)"
                                      : message.status === "failed" && message.refundStatus === "refunded"
                                        ? "FAILED (refunded)"
                                        : (message.status || "pending").toUpperCase()}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {message.sentAt || message.createdAt
                                    ? new Date(message.sentAt || message.createdAt).toLocaleString()
                                    : "-"}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                  {message.status === "failed" || hasMessageStatusDetails(message) ? (
                                    <div className="max-w-xs" title={getFailedMessageError(message).title}>
                                      <div>
                                        <span
                                          className={`font-medium ${
                                            message.status === "failed"
                                              ? "text-red-600 dark:text-red-400"
                                              : "text-amber-700 dark:text-amber-300"
                                          }`}
                                        >
                                          {getFailedMessageError(message).code}
                                        </span>:{" "}
                                        <span className="break-words">{getFailedMessageError(message).text}</span>
                                      </div>
                                      {getFailedMessageError(message).extra ? (
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                          {getFailedMessageError(message).extra}
                                        </div>
                                      ) : null}
                                      {message.refundStatus === "pending" && message.refundScheduledAt ? (
                                        <div className="text-xs text-orange-600 dark:text-orange-300 mt-1">
                                          Refund scheduled at {new Date(message.refundScheduledAt).toLocaleString()}
                                        </div>
                                      ) : null}
                                      {message.refundStatus === "refunded" && message.refundedAt ? (
                                        <div className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                                          Refunded at {new Date(message.refundedAt).toLocaleString()}
                                        </div>
                                      ) : null}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                {selectedCampaign?.showCredits !== false && (
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded">
                                      {message.creditUsed || message.credits || 1}
                                    </span>
                                  </td>
                                )}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default APIreport
